// BEGIN FILE: src/components/FutureEventAlerts.tsx
/**
 * Future Event Alert Cards
 * 
 * Displays Opportunity Windows and Caution Time Windows
 * for the next 6-12 months (2 years for marriage)
 */

import React, { useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import {
  Box,
  Card,
  Typography,
  Chip,
  Stack,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionGroup,
  Sheet,
  Divider,
  CircularProgress,
  Alert
} from '@mui/joy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { FutureEvent, FutureEventCategory } from '../utils/futureEventPredictor';
import {
  computeFutureEvents,
  groupEventsByCategory,
  getTopEvents,
  getCategoryInfo
} from '../utils/futureEventPredictor';
import type { DashaContext } from '../astro/feeds/types';

interface Props {
  birthISO: string;
  moonLongDeg: number;
  houseScores: Record<number, number>;
  natalPlacements: DashaContext['natalPlacements'];
  combustionMap?: DashaContext['combustionMap'];
  gender?: 'male' | 'female' | 'other';
  ascendantSign: number; // 1-based: 1=Aries, 2=Taurus, etc.
}

/**
 * Format time window for display
 */
function formatWindow(window: { from: string; to: string }): string {
  const from = DateTime.fromISO(window.from);
  const to = DateTime.fromISO(window.to);
  
  if (!from.isValid || !to.isValid) return 'Invalid dates';
  
  const fromStr = from.toFormat('dd MMM yyyy');
  const toStr = to.toFormat('dd MMM yyyy');
  
  // If same month and year, show compact format
  if (from.month === to.month && from.year === to.year) {
    return `${from.toFormat('dd')}-${to.toFormat('dd MMM yyyy')}`;
  }
  
  return `${fromStr} → ${toStr}`;
}

/**
 * Event Card Component
 */
function EventCard({ event }: { event: FutureEvent }) {
  const categoryInfo = getCategoryInfo(event.category);
  const [expanded, setExpanded] = useState(false);
  
  // Determine dynamic icon based on title sentiment
  let displayIcon = categoryInfo.icon;
  if (event.title === 'Partnership Harmony') {
    displayIcon = '💑'; // Couple with heart
  } else if (event.title === 'Physical Wellbeing') {
    displayIcon = '🏃'; // Runner
  } else if (event.title === 'Health Vitality') {
    displayIcon = '💪'; // Flexed bicep
  } else if (event.title === 'Legal Clarity') {
    displayIcon = '⚖️'; // Balance scale
  } else if (event.title === 'Father Support') {
    displayIcon = '👨'; // Man/father icon
  } else if (event.title === 'Mother Wellness') {
    displayIcon = '👩'; // Woman/mother icon
  }
  
  return (
    <Sheet
      variant="outlined"
      sx={{
        p: { xs: 1, sm: 1.5 },
        borderRadius: 'sm',
        borderLeft: `4px solid`,
        borderLeftColor: event.type === 'opportunity' ? 'success.500' : 'warning.500',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        overflow: 'visible',
        wordBreak: 'break-word',
        '&:hover': {
          bgcolor: 'background.level1'
        }
      }}
    >
      <Stack spacing={0.5} sx={{ width: '100%', minWidth: 0, overflow: 'visible' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', minWidth: 0, gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: '1 1 auto', minWidth: 0 }}>
            <Typography level="body-sm" sx={{ fontSize: '1.2rem', flexShrink: 0 }}>
              {displayIcon}
            </Typography>
            <Typography level="title-sm" fontWeight="lg" sx={{ wordWrap: 'break-word', overflowWrap: 'break-word', flex: '1 1 auto', minWidth: 0 }}>
              {event.title}
            </Typography>
          </Box>
          <Stack spacing={0.5} alignItems="flex-end">
            {event.highProbability && (
              <Chip
                size="sm"
                variant="solid"
                color={event.type === 'opportunity' ? 'success' : 'danger'}
                sx={{ fontSize: '0.65rem', height: '18px' }}
              >
                ⚡ HIGH PROBABILITY
              </Chip>
            )}
            <Chip
              size="sm"
              variant="soft"
              color={event.severity === 'positive' ? 'success' : 
                     event.severity === 'alert' ? 'danger' : 'warning'}
              sx={{ fontSize: '0.7rem', height: '20px' }}
            >
              {event.confidence.toFixed(0)}% confidence
            </Chip>
          </Stack>
        </Box>

        {/* Time Window */}
        <Typography level="body-xs" sx={{ color: 'text.tertiary', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
          📅 {formatWindow(event.window)}
          {event.isConsolidated && event.consolidatedCount && (
            <Chip
              size="sm"
              variant="soft"
              color="neutral"
              sx={{ fontSize: '0.65rem', height: '16px', ml: 0.5 }}
            >
              {event.consolidatedCount} periods merged
            </Chip>
          )}
        </Typography>

        {/* Rahu Peak Windows - Show if this is a consolidated event with Rahu periods */}
        {event.rahuPeakWindows && event.rahuPeakWindows.length > 0 && (
          <Box sx={{ mt: 0.5, p: 0.75, bgcolor: 'danger.softBg', borderRadius: 'sm', border: '1px solid var(--joy-palette-danger-outlinedBorder)', wordBreak: 'break-word' }}>
            <Typography level="body-xs" fontWeight="bold" sx={{ color: 'danger.700', mb: 0.25, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
              ⚡ Peak Manifestation Windows:
            </Typography>
            <Stack spacing={0.25}>
              {event.rahuPeakWindows.map((window, idx) => (
                <Typography key={idx} level="body-xs" sx={{ color: 'danger.600', fontSize: '0.7rem', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                  • {formatWindow(window)}
                </Typography>
              ))}
            </Stack>
          </Box>
        )}

        {/* Description */}
        <Typography level="body-sm" sx={{ mt: 0.5, lineHeight: 1.4, wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>
          {event.description.length > 150 && !expanded
            ? `${event.description.substring(0, 150)}...`
            : event.description}
        </Typography>

        {/* Expand toggle for long descriptions */}
        {event.description.length > 150 && (
          <Typography
            level="body-xs"
            sx={{ color: 'primary.500', cursor: 'pointer', mt: 0.5 }}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Show less' : 'Read more'}
          </Typography>
        )}

        {/* Evidence/Reasoning - Always show at least summary */}
        {event.evidence && event.evidence.length > 0 && (
          <Box sx={{ mt: 1, p: 1, bgcolor: 'background.level1', borderRadius: 'sm', borderLeft: '3px solid var(--joy-palette-primary-300)', wordBreak: 'break-word' }}>
            <Typography level="body-xs" fontWeight="bold" sx={{ mb: 0.5, color: 'primary.700', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
              💡 Why this alert?
            </Typography>
            <Stack spacing={0.25}>
              {(expanded ? event.evidence : event.evidence.slice(0, 2)).map((note, idx) => (
                <Typography key={idx} level="body-xs" sx={{ color: 'text.secondary', fontSize: '0.7rem', lineHeight: 1.3, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                  • {note}
                </Typography>
              ))}
              {!expanded && event.evidence.length > 2 && (
                <Typography
                  level="body-xs"
                  sx={{ color: 'primary.500', cursor: 'pointer', fontSize: '0.7rem', fontStyle: 'italic' }}
                  onClick={() => setExpanded(true)}
                >
                  +{event.evidence.length - 2} more reasons...
                </Typography>
              )}
            </Stack>
          </Box>
        )}

        {/* Remedies */}
        {event.remedies && expanded && (
          <Box sx={{ mt: 1, p: 1, bgcolor: 'background.level2', borderRadius: 'sm' }}>
            <Typography level="body-xs" fontWeight="bold" sx={{ mb: 0.5 }}>
              🕉️ Remedies:
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
              {event.remedies}
            </Typography>
          </Box>
        )}

        {/* Life Areas */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
          {event.lifeAreas.slice(0, 3).map(area => (
            <Chip
              key={area}
              size="sm"
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: '18px' }}
            >
              {area}
            </Chip>
          ))}
        </Box>
      </Stack>
    </Sheet>
  );
}

/**
 * Main Component
 */
export default function FutureEventAlerts(props: Props) {
  const {
    birthISO,
    moonLongDeg,
    houseScores,
    natalPlacements,
    combustionMap,
    gender,
    ascendantSign
  } = props;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [opportunities, setOpportunities] = useState<FutureEvent[]>([]);
  const [cautions, setCautions] = useState<FutureEvent[]>([]);

  // Compute future events
  React.useEffect(() => {
    let mounted = true;

    async function compute() {
      try {
        setLoading(true);
        setError(null);

        const result = await computeFutureEvents({
          birthISO,
          moonLongDeg,
          houseScores,
          natalPlacements,
          combustionMap,
          gender,
          includeMarriage: true,
          ascendantSign
        });

        if (mounted) {
          setOpportunities(result.opportunities);
          setCautions(result.cautions);
        }
      } catch (err: any) {
        console.error('[FutureEventAlerts] Error computing events:', err);
        if (mounted) {
          setError(err.message || 'Failed to compute future events');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    compute();

    return () => {
      mounted = false;
    };
  }, [
    birthISO, 
    moonLongDeg, 
    houseScores, // Remove JSON.stringify - use object directly (React will shallow compare)
    natalPlacements, // These should be memoized in parent (App.tsx)
    combustionMap,
    gender,
    ascendantSign
  ]);

  // Group events by category
  const groupedOpportunities = useMemo(() => 
    groupEventsByCategory(opportunities),
    [opportunities]
  );
  
  const groupedCautions = useMemo(() => 
    groupEventsByCategory(cautions),
    [cautions]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert color="danger" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
      <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
        {/* OPPORTUNITY WINDOWS CARD */}
        <Grid xs={12} md={6}>
          <Card
            variant="outlined"
            sx={{
              height: '100%',
              borderTop: '3px solid',
              borderTopColor: 'success.500',
              width: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            <Box sx={{ p: { xs: 0.375, sm: 2 }, width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
              <Stack spacing={1}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography level="h4" sx={{ fontSize: '1.1rem' }}>
                    🌟 Opportunity Windows
                  </Typography>
                  <Chip size="sm" variant="soft" color="success">
                    {opportunities.length}
                  </Chip>
                </Box>
                
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Favorable periods for growth and success (Next 6-12 months, 2 years for marriage)
                </Typography>

                <Divider sx={{ my: 1 }} />

                {/* Events List */}
                {opportunities.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                      No high-confidence opportunities detected in the near future.
                      Continue current practices and stay prepared.
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={1.5} sx={{ maxHeight: '600px', overflowY: 'auto', width: '100%', minWidth: 0 }}>
                    {getTopEvents(opportunities, 15).map(event => (
                      <EventCard key={event.id} event={event} />
                    ))}
                  </Stack>
                )}
              </Stack>
            </Box>
          </Card>
        </Grid>

        {/* CAUTION TIME WINDOWS CARD */}
        <Grid xs={12} md={6}>
          <Card
            variant="outlined"
            sx={{
              height: '100%',
              borderTop: '3px solid',
              borderTopColor: 'warning.500',
              width: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            <Box sx={{ p: { xs: 0.375, sm: 2 }, width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
              <Stack spacing={1}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography level="h4" sx={{ fontSize: '1.1rem' }}>
                    ⚠️ Caution Time Windows
                  </Typography>
                  <Chip size="sm" variant="soft" color="warning">
                    {cautions.length}
                  </Chip>
                </Box>
                
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Alert periods requiring vigilance and preventive measures
                </Typography>

                <Divider sx={{ my: 1 }} />

                {/* Events List */}
                {cautions.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                      No major caution periods detected. Maintain regular practices
                      and stay mindful.
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={1.5} sx={{ maxHeight: '600px', overflowY: 'auto', width: '100%', minWidth: 0 }}>
                    {getTopEvents(cautions, 15).map(event => (
                      <EventCard key={event.id} event={event} />
                    ))}
                  </Stack>
                )}
              </Stack>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
